import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import {
  setHasAgreed,
  saveDraftContract,
  clearCalculationBackendErrors,
  saveOncoCareContractInCache,
} from '../../../../redux/modules/oncoCareContract';
import OncoCareCalculator from '../../../../components/OncoCareCalculator';
import { getRiskAmounts, setAge, setPaymentFrequency, setRiskAmount, setWithChildren } from '../../../../redux/modules/oncoCareCalculation';
import InfoBlock from '../../../../components/InfoBlock';
import { contractCalculatorSchema } from '../../../../validations/contract/oncoCare';
import { getDiffInYearsFromNow, getEarliestDateFromArray } from '../../../../helpers/dates';
import useScrollToFormErrorElement from '../../../../hooks/useScrollToFormErrorElement';
import usePrevious from '../../../../hooks/usePrevious';
import Navigation from './Navigation';

const oncoCareDataSelector = (state) => state.oncoCareContract.contractData.oncoCareDetails;

const CalculationStep = ({ goPrevious, goNext, blockNextSteps, params }) => {
  const {
    isFetchingPrice,
    totalPrice,
    loader,
    actionName,
    handleOpenMobileSummary,
    isDeviceLgSize,
    setStepNavigationBox,
  } = params;

  const dispatch = useDispatch();
  const [submitCount, setSubmitCount] = useState(0);
  const previousCount = usePrevious(submitCount);

  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const isSavingCache = useSelector((state) => state.oncoCareContract.savingContractInCache);
  const oncoCareDetails = useSelector(oncoCareDataSelector);

  const {
    withChildren,
    paymentFrequencies,
    prices,
    paymentFrequency,
    isFetchingRiskAmounts,
    riskAmounts,
    riskAmount,
  } = oncoCareDetails;

  const hasAgreed = useSelector((state) => state.oncoCareContract.hasAgreed);
  const insuredPeople = useSelector((state) => state.oncoCareContract.contractData.insuredPeople);

  const formProps = useForm({
    defaultValues: {
      ...oncoCareDetails,
      hasAgreed,
    },
    validationSchema: contractCalculatorSchema,
  });

  const earliestAge = useMemo(() => {
    const existinginsuredPeople = insuredPeople || [];
    const birthdays = existinginsuredPeople.map((insuredPerson) => insuredPerson.birthday);
    const validBirthdays = birthdays.filter((date) => !!date);
    const earliestBirthday = getEarliestDateFromArray(validBirthdays) || null;
    return earliestBirthday ? getDiffInYearsFromNow(earliestBirthday) : 0;
  }, [insuredPeople]);

  useEffect(() => {
    setSubmitCount(formProps.formState.submitCount);
  }, [formProps.formState.submitCount]);

  useScrollToFormErrorElement(formProps.errors, submitCount, previousCount);

  const handleAgreementClick = () => {
    dispatch(setHasAgreed(!hasAgreed));
    const { errors, triggerValidation } = formProps;
    if (errors.hasAgreed) {
      triggerValidation({ name: 'hasAgreed' });
    }
  };

  const next = useCallback(() => {
    formProps.triggerValidation();
    dispatch(clearCalculationBackendErrors());
    if (isAuthenticated) {
      dispatch(saveDraftContract());
    }
    dispatch(saveOncoCareContractInCache()).then(
      () => goNext(),
      () => { },
    );
  }, [dispatch, formProps, goNext, isAuthenticated]);

  const handleNext = useCallback(() => {
    formProps.handleSubmit(next)();
  }, [formProps, next]);

  useEffect(() => {
    if (isEmpty(riskAmounts)) {
      dispatch(getRiskAmounts(actionName));
    }
  }, [dispatch, riskAmounts, actionName]);

  useEffect(() => {
    if (!isEmpty(formProps.errors) || !hasAgreed) {
      blockNextSteps();
    }
  }, [formProps.errors, hasAgreed, blockNextSteps]);

  const errorsLength = Object.keys(formProps.errors).length;

  const dispatchWithChildren = useCallback(
    (withChild) => dispatch(setWithChildren(withChild, actionName)), [
      dispatch,
      actionName,
    ],
  );

  const dispatchRiskAmount = useCallback((amount) => dispatch(setRiskAmount(amount, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchPaymentFrequency = useCallback(
    (frequency) => dispatch(setPaymentFrequency(frequency, actionName)), [
      dispatch,
      actionName,
    ],
  );

  const dispatchAge = useCallback((newAge) => dispatch(setAge(newAge, actionName)), [
    dispatch,
    actionName,
  ]);

  return (
    <div>
      {isSavingCache && loader}
      <Navigation
        handleNext={handleNext}
        errorsLength={errorsLength}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        totalPrice={totalPrice}
        goPrevious={goPrevious}
        isFetchingPrice={isFetchingPrice}
        handleOpenMobileSummary={handleOpenMobileSummary}
      />
      <OncoCareCalculator
        formProps={formProps}
        setAge={dispatchAge}
        age={earliestAge}
        setWithChildren={dispatchWithChildren}
        withChildren={withChildren}
        riskAmount={riskAmount}
        setRiskAmount={dispatchRiskAmount}
        riskAmounts={riskAmounts}
        isFetchingRiskAmounts={isFetchingRiskAmounts}
        isFetchingPrice={isFetchingPrice}
        prices={prices}
        paymentFrequencies={paymentFrequencies}
        setPaymentFrequency={dispatchPaymentFrequency}
        paymentFrequency={paymentFrequency}
        totalPrice={totalPrice}
        showWithChildrenCheckbox={false}
        oncoCareDetails={oncoCareDetails}
        showAgeInput={false}
        showNavigationButtons={false}
      />
      <InfoBlock
        formProps={formProps}
        handleAgreementClick={handleAgreementClick}
        hasAgreed={hasAgreed}
        showInfoBlock
        infoBlockText="oncoCareContract.termsOfService"
        agreementText="oncoCareContract.agreementDeclaration"
      />
    </div>
  );
};

CalculationStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  params: PropTypes.shape({
    data: PropTypes.shape({
      signer: PropTypes.shape({}),
    }).isRequired,
    isFetchingPrice: PropTypes.bool,
    totalPrice: PropTypes.number,
    loader: PropTypes.element.isRequired,
    actionName: PropTypes.string.isRequired,
    isDeviceLgSize: PropTypes.bool.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    setStepNavigationBox: PropTypes.func.isRequired,
  }),
};

CalculationStep.defaultProps = {
  params: {
    isFetchingPrice: false,
    totalPrice: null,
  },
};

export default CalculationStep;
